import * as React from "react";
import { merge, $ } from "glamor";
import { ICellEditorParams } from "ag-grid-community/dist/lib/interfaces/iCellEditor";
import classNames from "classnames";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "material-ui/CircularProgress";
import MenuItem from "@material-ui/core/MenuItem";
import _ from "lodash";
import Paper from "@material-ui/core/Paper";
import Downshift from "downshift";
import theme from "styles/theme";
import { RowNode } from "ag-grid-community/dist/lib/entities/rowNode";
import { format } from "lib/globalize-util";
import { ISuggestionItem } from "containers/product-collections/product-collection-cart";
import { getFilteredSuggestions, markupGenerator } from "./editor-helper";
import { SizingErrors, SizeCurveStatus } from "constants/product-collection";
import { ILookupRawData, IDependency } from "helpers/merchant/lookup/types";
import { generateStoreKey, transformDataToSuggestions } from "helpers/internal-lookups/size-curves-lookup";
import { List } from "react-virtualized/dist/commonjs/List";
import { CellMeasurer, CellMeasurerCache } from "react-virtualized/dist/commonjs/CellMeasurer";
import { KEYBOARD_KEYS } from "constants/keyboard";

enum EDIT_MODES {
    REPLACE,
    APPEND,
}
interface IProps extends ICellEditorParams {
    values?: string[];
    dependentUrlGetter?: any;
    getUrlDependencies?: (dependencies, data) => string[];
    getMissingDependencies: (data) => string[];
    internalGetter?: any;
    updateDependantField?: (node: RowNode, value: string) => void;
    dataGetter?: (value: string, data: ISuggestionItem[] | ILookupRawData[]) => string;
    labelGenerator?: (dependency: IDependency) => string;
    freeform?: boolean;
    getDisplayName: (dependencies: string[]) => void;
    missingDependenciesMessage?: (missingDependencies: string[]) => string;
    getSuggestionData?: () => ILookupRawData[];
    setSuggestionData?: (data: ILookupRawData[], storeKey: string) => void;
}
interface IState {
    rawData: ISuggestionItem[] | ILookupRawData[]; // Will have diff data type one from fe_lookup, one from lookup services
    inputValue: string;
    displaySuggestions: string[];
    filteredDisplaySuggestions: string[];
    editMode: EDIT_MODES;
    focus: boolean;
    selectedItem: string;
    highLightInputValue: string;
    setBottom: boolean;
    dependency: IDependency;
    isLoading?: boolean;
    displayMessage?: string;
    highLightIndex: number | null;
}

export const PAPER_HEIGHT = 200;
const CONTAINER_DEFAULT_WIDTH = 200;
const INPUT_PADDING_WIDTH = 25;
const KEY_DOWN = 40;
const KEY_UP = 38;
const EDITOR_DISPLAY = "dropdown_format";
export class NuRichSelectCellEditor extends React.PureComponent<IProps, IState> {
    startEditListener;
    endEditListener;
    inputRef: React.RefObject<{}>;
    isCellEditing: boolean = false;
    currentRowNode: RowNode;
    private _unmounted: boolean = false;
    private styles = merge(
        $(".editbox .container", {
            height: "64px",
            width: `${CONTAINER_DEFAULT_WIDTH}px`,
        }),
        $(" .MuiTextField-root", {
            height: "50px",
        }),
        $(".editbox .paper", {
            overflow: "auto",
            height: `${PAPER_HEIGHT}px`,
            width: "100%",
            marginLeft: "-17px",
            marginTop: "9px",
            border: `2px solid ${theme.palette.borderColor}`,
            boxSizing: "content-box",
            boxShadow: `0 2px 2px ${theme.palette.borderColor}`,
            borderRadius: "0 0 5px 5px",
        }),
        $(".editbox .bottom", {
            position: "absolute",
            top: `${-5 - PAPER_HEIGHT}px`,
        }),
        $(".editbox .MuiInputLabel-root", {
            color: "black",
            paddingTop: "5px",
            fontSize: "12px",
        }),
        $(".editbox .MuiInput-formControl", {
            marginTop: "21px",
        }),
        $(".editbox .inputField::after", {
            borderBottom: "0px",
        }),
        $(".editbox .MuiInput-underline::before", {
            borderBottom: "0px !important",
        }),
        $(".editbox .inputBox", {
            border: `1px solid ${theme.palette.borderColor}`,
            borderRadius: "3px",
            height: "7px",
        }),
        $(".editbox .Mui-focused .inputBox", {
            borderWidth: "0px",
        }),
        $(".editbox .inputField.Mui-focused", {
            height: "25px",
            border: `1px solid ${theme.palette.borderColor}`,
        }),
        $(".editbox .MuiMenuItem-root", {
            whiteSpace: "normal",
            paddingLeft: "25px",
            fontSize: "12px",
            minHeight: "44px",
        }),
        $(".editbox .message", {
            backgroundColor: theme.palette.lighterGrey,
            display: "flex",
            justifyContent: "center",
            height: "calc(100% - 16px)",
            margin: "8px",
        }),
        $(".editbox .message > p", {
            textAlign: "center",
            color: theme.palette.grey,
            alignSelf: "center",
        }),
        $(".editbox .loading", {
            display: "flex",
            height: "100%",
            alignItems: "center",
            justifyContent: "center",
            alignContent: "center",
        })
    );
    private takeDefaultValue = false;
    private defaultValue = "";
    private cache: CellMeasurerCache;

    constructor(props) {
        super(props);
        let editMode = EDIT_MODES.APPEND;
        let initValue = props.value || props.value === 0 ? `${props.value}` : "";
        this.defaultValue = initValue;
        if (props.eventKey === KEYBOARD_KEYS.BACKSPACE || props.eventKey === KEYBOARD_KEYS.DELETE) {
            initValue = "";
        } else if (props.charPress) {
            initValue = props.charPress;
            editMode = EDIT_MODES.REPLACE;
        }
        this.state = {
            rawData: [],
            inputValue: initValue,
            highLightInputValue: undefined,
            selectedItem: undefined,
            displaySuggestions: [],
            editMode,
            focus: false,
            setBottom: false,
            dependency: {},
            filteredDisplaySuggestions: [],
            highLightIndex: null,
        };
        this.inputRef = React.createRef();

        this.cache = new CellMeasurerCache({
            defaultHeight: 44,
            fixedWidth: true,
            minHeight: 44,
        });
    }

    processExternalGetter = ({ dependentUrlGetter, getUrlDependencies } = this.props) => {
        if (!dependentUrlGetter && typeof dependentUrlGetter !== "function") {
            return;
        }

        const { feLookupUrl, dependency } = dependentUrlGetter()(this.props.data.fields);
        const missingDependencies = getUrlDependencies && getUrlDependencies(dependency, this.props.data);
        if (missingDependencies && missingDependencies.length) {
            this.setState({
                dependency,
                isLoading: false,
                displayMessage: format("Please enter |requiredFields| for this line item.", {
                    requiredFields: missingDependencies.join(", "),
                }),
            });
            return;
        }

        this.setState({
            dependency,
            isLoading: true,
        });
        const options = {
            method: "GET",
        };

        fetch(feLookupUrl, options)
            .then((response) => response.json())
            .then((data) => {
                if (!this._unmounted && data && data.length > 0 && !data.status) {
                    let displayData = data;
                    if (typeof data[0] === "object") {
                        displayData = data.map((item) => item.Value);
                    }
                    const filteredDisplaySuggestions = getFilteredSuggestions(this.state.inputValue, displayData);
                    this.setState({
                        rawData: data,
                        displaySuggestions: displayData,
                        filteredDisplaySuggestions,
                        isLoading: false,
                    });
                } else {
                    this.setState({
                        isLoading: false,
                    });
                }
            })
            .catch((_error) => {
                this.setState({
                    rawData: [],
                    displaySuggestions: [],
                    filteredDisplaySuggestions: [],
                    isLoading: false,
                });
            });
    };

    get noResultsFoundMessage() {
        return format("No results found");
    }

    internalUpdateState = (result) => {
        if (!this._unmounted) {
            const displaySuggestions =
                typeof result[0] === "object"
                    ? result.map((suggestion) => suggestion.value || suggestion[EDITOR_DISPLAY])
                    : result;
            const filteredDisplaySuggestions = getFilteredSuggestions(this.state.inputValue, displaySuggestions);
            this.setState({
                rawData: result,
                displaySuggestions,
                filteredDisplaySuggestions,
                displayMessage: !displaySuggestions.length ? this.noResultsFoundMessage : "",
                isLoading: false,
            });
        }
    };

    processInternalGetter = (
        { internalGetter, getMissingDependencies, getSuggestionData, setSuggestionData, getDisplayName } = this.props
    ) => {
        if (!internalGetter && typeof internalGetter !== "function") {
            return;
        }
        const { interalLookup, dependency, lookupPlan } = internalGetter()(this.props.data.fields);
        const missingDependencies = getMissingDependencies(this.props.data);
        const { missingDependenciesMessage } = this.props;
        if (missingDependencies && missingDependencies.length) {
            const displayMessage = missingDependenciesMessage
                ? missingDependenciesMessage(missingDependencies)
                : undefined;
            this.setState({
                isLoading: false,
                dependency,
                displayMessage:
                    displayMessage ||
                    format("Please enter |requiredFields| for this line item.", {
                        requiredFields: missingDependencies.join(", "),
                    }),
            });
            return;
        }
        const suggestionData = getSuggestionData() || {};
        const storeKey = generateStoreKey(lookupPlan, dependency, this.props.colDef.field);
        // NOTE: use cache to reduce API call.
        if (suggestionData[storeKey] !== undefined) {
            this.internalUpdateState(suggestionData[storeKey]);
        } else {
            this.setState({ dependency, isLoading: true });
            interalLookup({ data: dependency, fieldName: this.props.colDef.field })
                .then((result) => {
                    const { suggestions } = transformDataToSuggestions(result, this.props.data.fields);
                    this.internalUpdateState(suggestions || []);
                    setSuggestionData(suggestions || [], storeKey);
                })
                .catch((error) => {
                    this.setState({
                        rawData: [],
                        displaySuggestions: [],
                        filteredDisplaySuggestions: [],
                        displayMessage:
                            error.codeText === SizingErrors.NoMatch || error.status >= 400
                                ? this.noResultsFoundMessage
                                : "",
                        isLoading: false,
                    });
                });
        }
    };

    componentDidMount() {
        const { values } = this.props;
        const { inputValue } = this.state;

        const cellPosition = this.props.eGridCell.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const { y } = cellPosition as any;
        const bottom = windowHeight - y < PAPER_HEIGHT && y > PAPER_HEIGHT + 110;

        this.setState({
            setBottom: bottom,
        });
        const suggestionData = values || [];
        if (suggestionData.length === 0) {
            // NOTE: we use internal OR external, maybe add some condition from shcema
            // to seperate the call. Even we accidentally pass both getter.
            this.processExternalGetter(this.props);
            this.processInternalGetter(this.props);
        } else {
            const filteredDisplaySuggestions = getFilteredSuggestions(inputValue, suggestionData);
            this.setState({
                displaySuggestions: suggestionData,
                filteredDisplaySuggestions,
            });
        }
        // NOTE: need to sign to a value for listners
        // otherwise, they can not be removed.
        // https://github.com/ag-grid/ag-grid/issues/1502
        this.startEditListener = this.startEdit.bind(this);
        this.endEditListener = this.endEdit.bind(this);

        this.props.api.addEventListener("cellEditingStarted", this.startEditListener);
        this.props.api.addEventListener("cellEditingStopped", this.endEditListener);
    }

    startEdit(e) {
        this.isCellEditing = true;
        this.currentRowNode = e.node;
    }

    endEdit(e) {
        this.isCellEditing = false;
        const { updateDependantField } = this.props;
        if (updateDependantField) {
            let { selectedItem: value } = this.state;
            const { filteredDisplaySuggestions } = this.state;
            if (this.state.displayMessage === this.noResultsFoundMessage) {
                value = SizingErrors.Generic;
            }
            if (!value && this.state.inputValue.length > 0) {
                const hasSuggestion = filteredDisplaySuggestions.length > 0;
                const isManual = this.defaultValue === SizeCurveStatus.Manual;
                value = hasSuggestion || isManual ? this.defaultValue : null;
                if (!value) {
                    const {
                        colDef: { colId },
                    } = e;
                    this.currentRowNode.setDataValue(colId, "");
                }
            }
            if (this.defaultValue !== value) {
                updateDependantField(this.currentRowNode, value);
            }
        }
    }

    generateReturnValue() {
        const { inputValue, rawData, filteredDisplaySuggestions, selectedItem, highLightIndex } = this.state;
        const { dataGetter, freeform } = this.props;
        let rawValue = selectedItem || inputValue || "";

        if (highLightIndex !== null) {
            rawValue = filteredDisplaySuggestions[highLightIndex];
        } else if (filteredDisplaySuggestions?.length === 1 && rawValue?.length > 0) {
            rawValue = filteredDisplaySuggestions[0];
        }
        let returnValue = rawValue;
        // NOTE: we have "empty" option
        if ((returnValue?.length ?? 0) === 0) {
            return returnValue;
        }
        if (dataGetter) {
            const code = dataGetter(rawValue, rawData);
            if (code !== undefined) {
                returnValue = code;
            } else {
                if (freeform) {
                    returnValue = inputValue;
                } else {
                    returnValue = this.defaultValue;
                }
            }
        } else if (!freeform) {
            const Option = filteredDisplaySuggestions.find((suggestion) => suggestion === returnValue);
            returnValue = Option || this.defaultValue;
        }
        return returnValue;
    }

    componentWillUnmount() {
        // NOTE: When editor close, remove listners at last.
        setTimeout(() => {
            this.props.api.removeEventListener("cellEditingStarted", this.startEditListener);
            this.props.api.removeEventListener("cellEditingStopped", this.endEditListener);
        }, 0);
        this._unmounted = true;
    }

    getValue() {
        // NOTE: try to handel all return data logic inside getValue,
        // eventListener may miss action such as tab or click outside editor.
        const returnValue = this.generateReturnValue();
        return returnValue;
    }

    isPopup = (): boolean => {
        return true;
    };

    handlePaste = (text: string) => {
        return "handled";
    };

    renderInput(inputProps) {
        const focusInputField = (input) => {
            if (input) {
                setTimeout(() => {
                    input.focus();
                }, 100);
            }
        };
        const { InputProps, ref, ...other } = inputProps;
        const inputWidth = parseInt(this.props.eGridCell.style.width.slice(0, -2), 10) - INPUT_PADDING_WIDTH;
        return (
            <TextField
                inputRef={focusInputField}
                InputProps={{
                    classes: {
                        root: "inputField",
                        input: "inputBox",
                    },
                    ...InputProps,
                }}
                style={{ width: `${inputWidth}px` }}
                {...other}
            />
        );
    }
    // NOTE: to make sure after click, the dropdown close.
    suggestionOnclick = (onClick, e) => {
        const { api } = this.props;
        onClick(e);
        process.nextTick(() => api.stopEditing());
    };

    renderSuggestion =
        ({ getItemProps, highlightedIndex }) =>
        ({ key, index, parent, style }) => {
            const { inputValue, filteredDisplaySuggestions } = this.state;
            const trimedInputValue = inputValue?.trim() || "";
            const suggestion = filteredDisplaySuggestions[index];
            const markup = markupGenerator(trimedInputValue, suggestion);
            const { onClick, ...itemProps } = getItemProps({ item: suggestion, index });

            return (
                <CellMeasurer cache={this.cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
                    <div style={style}>
                        <MenuItem
                            {...itemProps}
                            onClick={this.suggestionOnclick.bind(this, onClick)}
                            key={`${suggestion}-${index}`}
                            component="div"
                            style={{
                                backgroundColor:
                                    highlightedIndex === index
                                        ? theme.palette.selectedOptionBackground
                                        : theme.palette.canvasColor,
                            }}
                        >
                            {inputValue === "" ? suggestion : markup}
                        </MenuItem>
                    </div>
                </CellMeasurer>
            );
        };

    setDisplaySuggestions(newInputValue, displaySuggestions) {
        const newFilteredDisplaySuggestions = getFilteredSuggestions(newInputValue, displaySuggestions);
        if (!this._unmounted) {
            this.setState({
                filteredDisplaySuggestions: newFilteredDisplaySuggestions,
            });
        }
    }

    handleInputChange(event: React.ChangeEvent<{ value: string }>) {
        const newInputValue = event.target.value;
        const { displaySuggestions } = this.state;
        this.setState({
            inputValue: newInputValue,
        });
        const debouncedSetDisplaySuggestions = _.debounce(this.setDisplaySuggestions.bind(this), 100);
        debouncedSetDisplaySuggestions(newInputValue, displaySuggestions);
    }

    handleChange = (item: string) => {
        const { api } = this.props;
        this.setState({
            inputValue: undefined,
            selectedItem: item,
            highLightIndex: null,
        });
        setTimeout(() => api.stopEditing(), 0);
    };

    renderSuggestions = ({ filteredDisplaySuggestions, getItemProps, highlightedIndex }) => {
        const { setBottom, isLoading, displayMessage } = this.state;
        const hasSuggestions = !!filteredDisplaySuggestions.length && !isLoading;

        return (
            <div className="options" style={{ width: this.props.eGridCell.style.width }}>
                <Paper className={classNames({ bottom: setBottom, paper: true })} square={true}>
                    {isLoading && (
                        <div className="loading">
                            <CircularProgress />
                        </div>
                    )}
                    {!!displayMessage && (
                        <div className="message">
                            <p>{displayMessage}</p>
                        </div>
                    )}
                    {hasSuggestions && (
                        <List
                            deferredMeasurementCache={this.cache}
                            height={PAPER_HEIGHT}
                            rowCount={filteredDisplaySuggestions.length}
                            rowHeight={this.cache.rowHeight}
                            rowRenderer={this.renderSuggestion({ getItemProps, highlightedIndex })}
                            width={this.props.column.getActualWidth()}
                        />
                    )}
                </Paper>
            </div>
        );
    };

    render() {
        const { inputValue, selectedItem, dependency, filteredDisplaySuggestions } = this.state;
        const { headerName } = this.props.colDef;
        const { labelGenerator } = this.props;
        const label = labelGenerator && labelGenerator(dependency) !== "" ? labelGenerator(dependency) : headerName;

        return (
            <div {...this.styles} className="editbox" tabIndex={0} onKeyDown={this.handleKeyDown}>
                <Downshift
                    inputValue={inputValue || ""}
                    initialIsOpen={true}
                    onChange={this.handleChange}
                    selectedItem={selectedItem || ""}
                >
                    {({ getInputProps, getItemProps, getLabelProps, highlightedIndex }) => {
                        const placeholderText =
                            highlightedIndex !== null
                                ? filteredDisplaySuggestions[highlightedIndex]
                                : format("Enter |headerName|", { headerName });
                        const { onBlur, onChange, onFocus, ...inputProps } = getInputProps({
                            placeholder: placeholderText,
                            onKeyDown: this.handleInputKeyDown.bind(this, highlightedIndex),
                        });
                        return (
                            <div className="container" style={{ width: this.props.eGridCell.style.width }}>
                                {this.renderInput({
                                    fullWidth: true,
                                    label,
                                    InputLabelProps: getLabelProps(),
                                    InputProps: {
                                        startAdornment: selectedItem ? [selectedItem] : [],
                                        onBlur,
                                        onChange: (event) => {
                                            this.handleInputChange(event);
                                            onChange(event as React.ChangeEvent<HTMLInputElement>);
                                        },
                                        onFocus,
                                    },
                                    inputProps,
                                })}
                                {this.renderSuggestions({ filteredDisplaySuggestions, getItemProps, highlightedIndex })}
                            </div>
                        );
                    }}
                </Downshift>
            </div>
        );
    }

    handleInputKeyDown(highlightedIndex, event) {
        const { filteredDisplaySuggestions } = this.state;
        let index = highlightedIndex;

        if (event.keyCode === KEY_DOWN) {
            if (index === null || index === filteredDisplaySuggestions.length - 1) {
                index = 0;
            } else {
                index = index + 1;
            }
        } else if (event.keyCode === KEY_UP) {
            if (index === null || index === 0) {
                index = filteredDisplaySuggestions.length - 1;
            } else {
                index = index - 1;
            }
        }
        this.setState({
            highLightIndex: index,
        });
    }

    private handleKeyDown = (e: React.KeyboardEvent) => {
        const { editMode } = this.state;
        switch (e.key) {
            case KEYBOARD_KEYS.LEFT:
            case KEYBOARD_KEYS.RIGHT:
            case KEYBOARD_KEYS.UP:
            case KEYBOARD_KEYS.DOWN:
                // Suggestions will cause defaultPrevented to be true if using key events
                if (!e.defaultPrevented && editMode === EDIT_MODES.REPLACE) {
                    this.props.stopEditing();
                    this.navigateToCell(e);
                    e.preventDefault();
                }
                break;
            default:
        }
    };

    private navigateToCell = (e) => {
        const { api } = this.props;
        const focusedCell = api.getFocusedCell();
        api.clearRangeSelection();
        switch (e.keyCode) {
            case KEYBOARD_KEYS.RIGHT:
                api.tabToNextCell();
                break;
            case KEYBOARD_KEYS.LEFT:
                api.tabToPreviousCell();
                break;
            case KEYBOARD_KEYS.UP:
                if (focusedCell.rowIndex > 0) {
                    api.setFocusedCell(focusedCell.rowIndex - 1, focusedCell.column.getColId());
                }
                break;
            case KEYBOARD_KEYS.DOWN:
            case KEYBOARD_KEYS.ENTER:
                // covering "new line"
                if (e.ctrlKey) {
                    api.setFocusedCell(focusedCell.rowIndex + 1, focusedCell.column.getColId(), null);
                }
                break;
            default:
        }
    };
}
